// src/components/ServerDatabase/ServerDetailsModal.jsx
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { renderServerIcon } from './ServerCard';

const ServerDetailsModal = ({
  server,
  isOpen,
  onClose,
  onCopyIp,
  onConnectRcon
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !server) return null;

  const fullAddress = server.port ? `${server.ip}:${server.port}` : server.ip;

  let vulnClass = 'vuln-rcon';
  if (server.vulnType === 'auth') vulnClass = 'vuln-auth';
  if (server.vulnType === 'op') vulnClass = 'vuln-op';

  const dataRoute = isEn ? '/en/server-data-extraction' : '/server-data-extraction';

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className="sdb-modal-backdrop" onClick={handleBackdropClick}>
      <div className="sdb-modal-card">
        <button
          type="button"
          className="sdb-modal-close-btn"
          onClick={onClose}
          title={isEn ? 'Close (Esc)' : 'Закрыть (Esc)'}
        >
          <svg viewBox="0 0 24 24">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Заголовок модалки */}
        <div className="sdb-modal-header-wrap">
          <div className="sdb-server-icon-badge sdb-modal-avatar">
            {renderServerIcon(server.iconType, server.iconUrl)}
          </div>
          <div className="sdb-modal-header-text">
            <h2 className="sdb-modal-title">
              {fullAddress}
            </h2>
            <div className="sdb-modal-sub">
              {server.name} • {isEn ? 'Ping:' : 'Пинг:'} {server.ping} ms
            </div>
          </div>
        </div>

        {/* Minecraft MOTD */}
        <div className="sdb-server-motd-box sdb-modal-motd">
          <div
            className="sdb-motd-line"
            dangerouslySetInnerHTML={{ __html: server.motdLine1 }}
          />
          <div
            className="sdb-motd-line"
            dangerouslySetInnerHTML={{ __html: server.motdLine2 }}
          />
        </div>

        {/* Сводка уязвимости */}
        <div className="sdb-modal-vuln-box">
          <div className="sdb-modal-vuln-head">
            <span className="sdb-modal-vuln-label">
              {isEn ? 'Security Status' : 'Статус безопасности'}
            </span>
            <span className={`sdb-meta-badge ${vulnClass}`}>{server.vulnBadge}</span>
          </div>
          <p className="sdb-modal-vuln-desc">
            {server.vulnDetail}
          </p>
        </div>

        {/* Сетка параметров 2x2 */}
        <div className="sdb-modal-params-grid">
          <div className="sdb-modal-param-card">
            <div className="sdb-modal-param-title">
              {isEn ? 'Version / Protocol' : 'Версия / Протокол'}
            </div>
            <div className="sdb-modal-param-val">
              {server.version}
            </div>
          </div>

          <div className="sdb-modal-param-card">
            <div className="sdb-modal-param-title">
              {isEn ? 'Server Core' : 'Ядро сервера'}
            </div>
            <div className="sdb-modal-param-val">
              {server.core}
            </div>
          </div>

          <div className="sdb-modal-param-card">
            <div className="sdb-modal-param-title">
              {isEn ? 'Discovered Ports' : 'Обнаруженные порты'}
            </div>
            <div className="sdb-modal-param-val sdb-modal-param-ports">
              {server.openPorts ? server.openPorts.join(', ') : `${server.port} Game`}
            </div>
          </div>

          <div className="sdb-modal-param-card">
            <div className="sdb-modal-param-title">
              {isEn ? 'Plugins Count' : 'Количество плагинов'}
            </div>
            <div className="sdb-modal-param-val">
              {server.pluginsCount} {isEn ? 'modules' : 'модулей'}
            </div>
          </div>
        </div>

        {/* Кнопки перехода */}
        <div className="sdb-modal-actions-row">
          <button
            type="button"
            className="sdb-action-btn-primary sdb-modal-action-primary"
            onClick={() => {
              onClose();
              onConnectRcon(fullAddress);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <polyline points="4 17 10 11 4 5" />
              <line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            <span>{isEn ? 'Open in RCON Console' : 'Открыть в RCON Консоли'}</span>
          </button>

          <div className="sdb-modal-secondary-group">
            <Link
              to={`${dataRoute}?ip=${encodeURIComponent(fullAddress)}`}
              className="sdb-action-btn-secondary sdb-modal-action-secondary"
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" fill="none" style={{ width: 15, height: 15 }}>
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
              </svg>
              <span>{isEn ? 'Extract Data' : 'Извлечь данные'}</span>
            </Link>

            <button
              type="button"
              className="sdb-action-btn-secondary sdb-modal-action-secondary"
              onClick={() => onCopyIp(fullAddress)}
              title={isEn ? 'Copy IP:PORT' : 'Скопировать IP'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>{isEn ? 'Copy IP' : 'Скопировать IP'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServerDetailsModal;
